import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

/**
 * Servicio para gestión de movimientos de caja
 * Maneja comunicación con API del backend
 */
class CashMovementsService {
  constructor() {
    this.api = axios.create({
      baseURL: `${API_URL}/cash-movements`
    });

    // Interceptor para agregar token de autenticación
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('adminToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  // Obtener headers de autenticación
  getAuthHeaders() {
    const token = localStorage.getItem('adminToken');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // Obtener movimientos de caja con filtros
  async getCashMovements(params = {}) {
    try {
      const response = await this.api.get('/', { params });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al cargar movimientos de caja');
      throw new Error(errorMessage);
    }
  }

  // Obtener movimientos por rango de fechas
  async getCashMovementsByDateRange(startDate, endDate, sede = null) {
    try {
      const params = {
        startDate,
        endDate
      };
      
      if (sede) {
        params.sede = sede;
      }

      const response = await this.api.get('/date-range', { params });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al cargar movimientos por fecha');
      throw new Error(errorMessage);
    }
  }

  // Obtener movimientos por sede
  async getCashMovementsBySede(sede) {
    try {
      const response = await this.api.get(`/sede/${sede}`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al cargar movimientos por sede');
      throw new Error(errorMessage);
    }
  }

  // Obtener estadísticas de movimientos
  async getCashMovementsStats(params = {}) {
    try {
      const response = await this.api.get('/stats', { params });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al cargar estadísticas');
      throw new Error(errorMessage);
    }
  }

  // Obtener movimiento por ID
  async getCashMovementById(id) {
    try {
      const response = await this.api.get(`/${id}`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al cargar movimiento');
      throw new Error(errorMessage);
    }
  }

  // Exportar movimientos a Excel
  async exportCashMovements(params = {}) {
    try {
      const response = await this.api.get('/export', {
        params,
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al exportar movimientos');
      throw new Error(errorMessage);
    }
  }

  // Crear movimiento de caja manual
  async createCashMovement(movementData) {
    try {
      const response = await this.api.post('/', movementData);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al crear movimiento');
      throw new Error(errorMessage);
    }
  }

  // Actualizar movimiento de caja
  async updateCashMovement(id, movementData) {
    try {
      const response = await this.api.put(`/${id}`, movementData);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al actualizar movimiento');
      throw new Error(errorMessage);
    }
  }

  // Eliminar movimiento de caja
  async deleteCashMovement(id) {
    try {
      await this.api.delete(`/${id}`);
      return true;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al eliminar movimiento');
      throw new Error(errorMessage);
    }
  }

  // Obtener detalles de asientos para un movimiento específico
  async getSeatDetails(movementId) {
    try {
      const response = await this.api.get(`/${movementId}/seat-details`);
      return response.data;
    } catch (error) {
      const errorMessage = this.getErrorMessage(error, 'Error al obtener detalles del asiento');
      throw new Error(errorMessage);
    }
  }

  // Manejar errores de API
  getErrorMessage(error, defaultMessage) {
    if (error.response) {
      // Error del servidor
      const { status, data } = error.response;
      
      if (status === 401) {
        return 'No tienes autorización para realizar esta acción';
      }
      
      if (status === 403) {
        return 'No tienes permisos para acceder a esta información';
      }
      
      if (status === 404) {
        return 'Movimiento no encontrado';
      }
      
      if (status === 422 && data?.message) {
        return data.message;
      }
      
      if (data?.message) {
        return data.message;
      }
    }
    
    if (error.request) {
      return 'No se pudo conectar con el servidor. Verifica tu conexión a internet.';
    }
    
    return defaultMessage;
  }
}

// Exportar instancia singleton
export const cashMovementsService = new CashMovementsService();
export default cashMovementsService;